import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import {
  Allocations,
  Cart,
  DistributionBasicPlanResponse,
  PreOrders,
  ProductSKU,
  ViewMode,
} from "../../../redux/distributionSlice";
import {
  useLazyGetDistributionBasicPlanQuery,
  useSaveCartDistributionPlanMutation,
} from "../../../redux/services/DistributionService";
import GridView from "./GridView";
import Header from "./Header";
import "./index.css";
import DistributionModal from "./DistributionModal";
import BreadCrumbs from "../../../components/BreadCrumbs";
import ErrorScreen from "../../../components/ErrorScreen";
import AllocationTableLoading from "../../../components/AllocationTableLoading";

interface RTKErrorResponse {
  status: string;
  originalStatus?: number;
  data?: any;
  error?: string;
}

const DistributionPlan: React.FC = () => {
  const RestID = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );
  const [productionSKUs, setProductionSKUs] = useState<ProductSKU[]>([]);
  const [carts, setCarts] = useState<Cart[]>([]);
  const [preOrders, setPreOrders] = useState<PreOrders>({});
  const [allocations, setAllocations] = useState<Allocations>({});
  const [originalAllocations, setOriginalAllocations] = useState<Allocations>(
    {}
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedSKU, setSelectedSKU] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [cartSearch, setCartSearch] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [priorityFilter, setPriorityFilter] = useState<string>("All");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cartTypeFilter, setCartTypeFilter] = useState<string>("All");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [bulkValue, setBulkValue] = useState("");
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [isLoadingNewDate, setIsLoadingNewDate] = useState<boolean>(false);
  const [isDistributionModalOpen, setIsDistributionModalOpen] = useState(false);
  const [hasNegativeAvailable, setHasNegativeAvailable] = useState(false);
  const [hasNoDistributionPlan, setHasNoDistributionPlan] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isInitialPlan, setIsInitialPlan] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [plannedAllocations, setPlannedAllocations] = useState<Allocations>({});

  const [
    triggerDistributionPlan,
    { data: apiData, isLoading, isUninitialized, isError, error },
  ] = useLazyGetDistributionBasicPlanQuery();

  const [saveCartDistributionPlan, { isLoading: isSaving }] =
    useSaveCartDistributionPlanMutation();

  // Track unsaved changes
  useEffect(() => {
    const changesExist =
      JSON.stringify(allocations) !== JSON.stringify(originalAllocations);
    setHasUnsavedChanges(changesExist);
  }, [allocations, originalAllocations]);

  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toISOString().split("T")[0];
    setSelectedDate(formattedDate);
  }, []);

  // Handle error state from the query hook
  useEffect(() => {
    if (isError && error) {
      const typedError = error as RTKErrorResponse;

      if (typedError.originalStatus === 404) {
        if (typeof typedError.data === "string") {
          setErrorMessage(typedError.data);
        } else if (typedError.data?.message) {
          setErrorMessage(typedError.data.message);
        } else {
          setErrorMessage("No distribution plan found for the selected date");
        }
        setHasNoDistributionPlan(true);

        setProductionSKUs([]);
        setCarts([]);
        setPreOrders({});
        setAllocations({});
        setOriginalAllocations({});
      } else {
        setErrorMessage("Failed to load distribution plan. Please try again.");
      }
    }
  }, [isError, error]);

  useEffect(() => {
    if (RestID && selectedDate) {
      setIsLoadingNewDate(true);
      setHasNoDistributionPlan(false);
      setErrorMessage(null); // Clear error message at start

      triggerDistributionPlan({ restaurantId: RestID, date: selectedDate })
        .unwrap()
        .then((data) => {
          if (data) {
            transformApiData(data);
          }
        })
        .catch((error: RTKErrorResponse) => {
          if (error.originalStatus === 404) {
            setHasNoDistributionPlan(true);
            setProductionSKUs([]);
            setCarts([]);
            setPreOrders({});
            setAllocations({});
            setOriginalAllocations({});

            if (typeof error.data === "string") {
              setErrorMessage(error.data);
            } else if (error.data?.message) {
              setErrorMessage(error.data.message);
            } else {
              setErrorMessage(
                "No distribution plan found for the selected date"
              );
            }
          } else {
            setErrorMessage(
              "Failed to load distribution plan. Please try again."
            );
            setHasNoDistributionPlan(false); // Don't set this to true for non-404 errors
          }
        })
        .finally(() => {
          setIsLoadingNewDate(false);
        });
    }
  }, [RestID, selectedDate, triggerDistributionPlan]);

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setHasNoDistributionPlan(false);
    setErrorMessage(null);
  };

  const transformApiData = (data: DistributionBasicPlanResponse) => {
    const distributionModel = data?.cartDistributionModel;

    const productMap = new Map();

    distributionModel?.carts.forEach((cart) => {
      cart.allocations.forEach((allocation) => {
        const key = `${allocation.productItemId}-${allocation.portionSize}`;
        if (!productMap.has(key)) {
          productMap.set(key, {
            productItemId: allocation.productItemId,
            productName: allocation.displayName,
            portionSize: allocation.portionSize,
            unitPrice: allocation.price,
            menuItemType: allocation.menuItemType,
            productAbbreviation: allocation.abbreviation,
          });
        }
      });
    });

    const transformedSKUs: ProductSKU[] = [];

    productMap.forEach((productInfo) => {
      const availableItem = distributionModel.availableItems.find(
        (item) =>
          item.productItemId === productInfo.productItemId &&
          item.portionSize === productInfo.portionSize
      );

      if (availableItem) {
        const allocationDetails = distributionModel.carts.flatMap((cart) =>
          cart.allocations.filter(
            (allocation) =>
              allocation.productItemId === productInfo.productItemId &&
              allocation.portionSize === productInfo.portionSize
          )
        );

        const totalAllocated = allocationDetails.reduce(
          (sum, allocation) => sum + allocation.directIssues,
          0
        );

        const totalCartReserved = allocationDetails.reduce(
          (sum, allocation) => sum + allocation.cartReserved,
          0
        );

        const restaurantReserved = availableItem.restaurantReserved || 0;

        transformedSKUs.push({
          id: `${productInfo.productItemId}-${productInfo.portionSize}`,
          name: productInfo.productName || productInfo.productItemId,
          portionSize: productInfo.portionSize,
          totalProduced: availableItem.availableCount,
          allocated: totalAllocated,
          available: availableItem.availableCount,
          unitPrice: productInfo.unitPrice,
          menuItemType: productInfo.menuItemType || "",
          restaurantReserved: restaurantReserved,
          totalCartReserved: totalCartReserved,
          abbreviation: productInfo.productAbbreviation,
        });
      }
    });

    // Transform carts
    const transformedCarts: Cart[] = distributionModel.carts.map((cart) => ({
      id: cart.cartId,
      name: cart.cartName,
      abbreviation: cart.abbreviation,
      priority: cart.priority || "",
      totalItems: cart.allocations.reduce(
        (sum, allocation) =>
          sum + allocation.directIssues + allocation.cartReserved,
        0
      ),
      isOrderPlaced: cart.isOrderPlaced,
    }));

    // Transform preOrders (cartReserved)
    const transformedPreOrders: PreOrders = {};
    distributionModel.carts.forEach((cart) => {
      transformedPreOrders[cart.cartId] = {};
      cart.allocations.forEach((allocation) => {
        const uniqueId = `${allocation.productItemId}-${allocation.portionSize}`;
        transformedPreOrders[cart.cartId][uniqueId] = allocation.cartReserved;
      });
    });

    // Transform allocations (directIssues)
    const transformedAllocations: Allocations = {};
    distributionModel.carts.forEach((cart) => {
      cart.allocations.forEach((allocation) => {
        const uniqueId = `${allocation.productItemId}-${allocation.portionSize}`;
        if (!transformedAllocations[uniqueId]) {
          transformedAllocations[uniqueId] = {};
        }
        transformedAllocations[uniqueId][cart.cartId] = allocation.directIssues;
      });
    });

    const transformedPlannedAllocations: Allocations = {};
    distributionModel.carts.forEach((cart) => {
      cart.allocations.forEach((allocation) => {
        const uniqueId = `${allocation.productItemId}-${allocation.portionSize}`;
        if (!transformedPlannedAllocations[uniqueId]) {
          transformedPlannedAllocations[uniqueId] = {};
        }
        transformedPlannedAllocations[uniqueId][cart.cartId] =
          allocation.prevDirectIssuesCount;
      });
    });

    setErrorMessage(null);
    setHasNoDistributionPlan(false);

    setProductionSKUs(transformedSKUs);
    setCarts(transformedCarts);
    setPreOrders(transformedPreOrders);
    setPlannedAllocations(transformedPlannedAllocations);
    setAllocations(transformedAllocations);
    setOriginalAllocations(JSON.parse(JSON.stringify(transformedAllocations)));
    setHasUnsavedChanges(false);

    if (transformedSKUs.length > 0) {
      setSelectedSKU(transformedSKUs[0].id);
    }

    setIsInitialPlan(data.isInitialPlan);
  };


  // Handle save changes
  const handleSaveChanges = useCallback(async () => {
    if (!apiData || isSaving) return;

    try {
      const distributionModel = apiData.cartDistributionModel;

      // Prepare the request data
      const requestData = {
        restaurantId: RestID,
        availableItems: distributionModel.availableItems,
        carts: distributionModel.carts.map((cart) => {
          const cartAllocations = Object.entries(allocations)
            .filter(([productId]) => {
              const baseProductId = productId.split("-")[0];
              return cart.allocations.some(
                (allocation) => allocation.productItemId === baseProductId
              );
            })
            .map(([productId, cartAllocations]) => {
              const baseProductId = productId.split("-")[0];
              const portionSize = productId.split("-")[1] as
                | "SMALL"
                | "REGULAR"
                | "LARGE";
              const allocation = cart.allocations.find(
                (a) =>
                  a.productItemId === baseProductId &&
                  a.portionSize === portionSize
              );

              return {
                productItemId: baseProductId,
                productName: allocation?.displayName || "",
                portionSize: portionSize,
                directIssues: cartAllocations[cart.cartId] || 0,
                reservedIssues: allocation?.cartReserved || 0,
                totalIssues: allocation?.totalIssues || 0,
              };
            });

          return {
            cartId: cart.cartId,
            cartName: cart.cartName,
            allocations: cartAllocations,
            isOrderPlaced: cart.isOrderPlaced,
          };
        }),
      };

      const result = await saveCartDistributionPlan(requestData).unwrap();

      if (result) {
        toast.success("Changes saved successfully!");
        setOriginalAllocations({ ...allocations });
        setHasUnsavedChanges(false);

        // Refetch the data to get updated isInitialPlan status
        try {
          const refreshedData = await triggerDistributionPlan({
            restaurantId: RestID,
            date: selectedDate,
          }).unwrap();

          if (refreshedData) {
            transformApiData(refreshedData);
          }
        } catch (refetchError) {
          setIsInitialPlan(false);
        }
      }
    } catch (error) {
      toast.error("Failed to save changes. Please try again.");
    }
  }, [
    apiData,
    allocations,
    isSaving,
    RestID,
    saveCartDistributionPlan,
    triggerDistributionPlan,
    selectedDate,
  ]);

  // Filtered data
  const filteredSKUs = useMemo(() => {
    return productionSKUs.filter(
      (sku) =>
        sku.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sku.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sku.portionSize.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [productionSKUs, searchTerm]);

 const filteredCarts = useMemo(() => {
  const filtered = carts.filter((cart) => {
    const matchesSearch =
      cart.name.toLowerCase().includes(cartSearch.toLowerCase()) ||
      cart.id.toLowerCase().includes(cartSearch.toLowerCase());
    const matchesPriority =
      priorityFilter === "All" || cart.priority === priorityFilter;
    const matchesType =
      cartTypeFilter === "All" || cart.name.includes(cartTypeFilter);
    return matchesSearch && matchesPriority && matchesType;
  });

  // Sort by priority: High -> Medium -> Low -> Empty string
  return filtered.sort((a, b) => {
    const priorityOrder: Record<string, number> = {
      'High': 1,
      'Medium': 2,
      'Low': 3,
      '': 4 
    };
    
    const aPriority = a.priority || '';
    const bPriority = b.priority || '';
    
    return priorityOrder[aPriority] - priorityOrder[bPriority];
  });
}, [carts, cartSearch, priorityFilter, cartTypeFilter]);

  const updateAllocation = useCallback(
    (productItemId: string, cartId: string, value: number | string) => {
      if (isLoadingNewDate) return;

      // Check if the cart has order placed
      const cart = carts.find((c) => c.id === cartId);
      if (cart?.isOrderPlaced) return;

      const numValue = typeof value === "string" ? parseInt(value) || 0 : value;
      setAllocations((prev) => ({
        ...prev,
        [productItemId]: {
          ...prev[productItemId],
          [cartId]: Math.max(0, numValue),
        },
      }));
    },
    [isLoadingNewDate, carts]
  );

  const getTotalAllocated = useCallback(
    (productItemId: string): number => {
      return Object.values(allocations[productItemId] || {}).reduce(
        (sum: number, val: number) => sum + val,
        0
      );
    },
    [allocations]
  );

  const toggleCellSelection = useCallback(
    (productItemId: string, cartId: string) => {
      if (isLoadingNewDate) return;

      // Check if the cart has order placed
      const cart = carts.find((c) => c.id === cartId);
      if (cart?.isOrderPlaced) return;

      const cellKey = `${productItemId}-${cartId}`;
      const newSelection = new Set(selectedCells);
      if (newSelection.has(cellKey)) {
        newSelection.delete(cellKey);
      } else {
        newSelection.add(cellKey);
      }
      setSelectedCells(newSelection);
    },
    [selectedCells, isLoadingNewDate, carts]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const applyBulkUpdate = useCallback(() => {
    if (isLoadingNewDate || !bulkValue || selectedCells.size === 0) return;

    const newAllocations = { ...allocations };
    selectedCells.forEach((cellKey) => {
      const [productItemId, cartId] = cellKey.split("-");
      if (!newAllocations[productItemId]) newAllocations[productItemId] = {};
      newAllocations[productItemId][cartId] = parseInt(bulkValue) || 0;
    });

    setAllocations(newAllocations);
    setSelectedCells(new Set());
    setBulkValue("");
  }, [bulkValue, selectedCells, allocations, isLoadingNewDate]);

  // Updated refreshData function with confirmation dialog
  const refreshData = useCallback(async () => {
    // Check for unsaved changes and show confirmation dialog
    if (hasUnsavedChanges) {
      const result = await Swal.fire({
        title: "Unsaved Changes",
        text: "You have unsaved changes. Refreshing will lose these changes. Continue?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Refresh",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
      });

      // If user cancels, don't proceed with refresh
      if (!result.isConfirmed) {
        return;
      }
    }

    try {
      setIsLoadingNewDate(true);
      setHasNoDistributionPlan(false);
      setErrorMessage(null); // Clear error message at start

      const result = await triggerDistributionPlan({
        restaurantId: RestID,
        date: selectedDate,
      }).unwrap();

      if (result) {
        transformApiData(result);
      }
    } catch (error: any) {
      if (error.originalStatus === 404) {
        setHasNoDistributionPlan(true);
        setProductionSKUs([]);
        setCarts([]);
        setPreOrders({});
        setAllocations({});
        setOriginalAllocations({});

        if (typeof error.data === "string") {
          setErrorMessage(error.data);
        } else if (error.data?.message) {
          setErrorMessage(error.data.message);
        } else {
          setErrorMessage("No distribution plan found for the selected date");
        }
      } else {
        setErrorMessage("Failed to load distribution plan. Please try again.");
        setHasNoDistributionPlan(false); // Don't set this to true for non-404 errors
      }
    } finally {
      setIsLoadingNewDate(false);
    }
  }, [triggerDistributionPlan, RestID, selectedDate, hasUnsavedChanges]);

  useEffect(() => {
    const hasNegative = productionSKUs.some((sku) => {
      const totalAllocated = getTotalAllocated(sku.id);
      const preOrdersTotal = Object.values(preOrders).reduce(
        (total, cartPreOrders) => total + (cartPreOrders[sku.id] || 0),
        0
      );
      return (sku.available ?? 0) - totalAllocated - preOrdersTotal < 0;
    });
    setHasNegativeAvailable(hasNegative);
  }, [productionSKUs, getTotalAllocated, preOrders]);

  // Calculate number of carts and products for loading component
  const numCarts = carts.length || 3;
  const numProducts = productionSKUs.length || 5;

  // const handleDistributionSuccess = useCallback((cartIds: string[]) => {
  //   setCarts((prev) =>
  //     prev.map((cart) => {
  //       if (cartIds.includes(cart.id)) {
  //         return { ...cart, isOrderPlaced: true };
  //       }
  //       return cart;
  //     })
  //   );
  // }, []);

  // Check if we have valid data to display
  const hasValidData = productionSKUs.length > 0 && carts.length > 0;

  const handleModalClose = useCallback(() => {
  setIsDistributionModalOpen(false);
  // Refresh data when modal closes to sync with server
  refreshData();
}, [refreshData]);

// Update the existing handler
const handleDistributionSuccess = useCallback((cartIds: string[]) => {
  setCarts((prev) =>
    prev.map((cart) => {
      if (cartIds.includes(cart.id)) {
        return { ...cart, isOrderPlaced: true };
      }
      return cart;
    })
  );
  // No need to call refreshData here since handleModalClose will handle it
}, []);

  return (
    <div>
      <BreadCrumbs hasSubMenus={true} />
      <div className="distribution-plan-container">
        <Header
          viewMode={viewMode}
          setViewMode={setViewMode}
          onRefresh={refreshData}
          onSave={handleSaveChanges}
          onOpenDistribution={() => setIsDistributionModalOpen(true)}
          onDateChange={handleDateChange}
          selectedDate={selectedDate}
          isLoading={isLoadingNewDate}
          isSaving={isSaving}
          hasChanges={hasUnsavedChanges}
          hasNegativeAvailable={hasNegativeAvailable}
          isInitialPlan={isInitialPlan}
        />

        <DistributionModal
          isOpen={isDistributionModalOpen}
         onClose={handleModalClose}
          carts={carts}
          products={productionSKUs}
          allocations={allocations}
          preOrders={preOrders}
          onDistributionSuccess={handleDistributionSuccess}
        />

        {/* Show loading component during initial load or date changes */}
        {(isLoading && isUninitialized) || isLoadingNewDate ? (
          <AllocationTableLoading
            numCarts={numCarts}
            numProducts={numProducts}
          />
        ) : (hasNoDistributionPlan || errorMessage) && !hasValidData ? (
          <ErrorScreen onRetry={refreshData} errorMessage={errorMessage} />
        ) : hasValidData ? (
          <GridView
            skus={filteredSKUs}
            carts={filteredCarts}
            allocations={allocations}
            preOrders={preOrders}
            selectedCells={selectedCells}
            searchTerm={searchTerm}
            cartSearch={cartSearch}
            setSearchTerm={setSearchTerm}
            setCartSearch={setCartSearch}
            toggleCellSelection={toggleCellSelection}
            updateAllocation={updateAllocation}
            getTotalAllocated={getTotalAllocated}
            isLoading={isLoadingNewDate}
            isReadOnly={isLoadingNewDate}
            originalAllocations={originalAllocations}
            plannedAllocations={plannedAllocations}
          />
        ) : null}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </div>
  );
};

export default DistributionPlan;
